'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AtSignIcon, KeySquareIcon, BuildingIcon } from 'lucide-react';
import { PhoneInput } from '@/components/ui/phone-input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupPassword } from '@/components/ui/input-group';
import { Combobox } from '@/components/ui/combobox';
import { DatePicker } from '@/components/ui/date-picker';
import { useMeStore, useMetadataStore } from '@/store';
import { getErrorMessage, setValidationErrors } from '@/lib/utils';
import { UserType, UpdateProfileParams } from '@/types/user';
import { EMAIL_REGEX } from '@/constants';
import { GenderEnum } from '@/enums';
type PropsType = {
  user: UserType;
  onSuccess?: () => void;
  formId?: string;
  onPendingChange?: (pending: boolean) => void;
};
const UpdateProfileForm = t0 => {
  const $ = _c(161);
  const {
    user,
    onSuccess,
    formId,
    onPendingChange
  } = t0;
  const t = useTranslations();
  const {
    editProfile
  } = useMeStore();
  const metadata = useMetadataStore(_temp);
  const [loading, setLoading] = useState(false);
  let t1;
  if ($[0] !== t) {
    t1 = t("male");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      label: t1,
      value: GenderEnum.MALE
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("female");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = {
      label: t3,
      value: GenderEnum.FEMALE
    };
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t) {
    t5 = t("diverse");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = {
      label: t5,
      value: GenderEnum.DIVERSE
    };
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t2 || $[13] !== t4 || $[14] !== t6) {
    t7 = [t2, t4, t6];
    $[12] = t2;
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const genderOptions = t7;
  let t8;
  if ($[16] !== metadata?.timezones) {
    t8 = metadata?.timezones ?? [];
    $[16] = metadata?.timezones;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t8) {
    t9 = t8.map(_temp2);
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const timezoneOptions = t9;
  let t10;
  if ($[20] !== metadata?.currencies) {
    t10 = metadata?.currencies ?? [];
    $[20] = metadata?.currencies;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10) {
    t11 = t10.map(_temp3);
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const currencyOptions = t11;
  let t12;
  let t13;
  if ($[24] !== t) {
    const formSchema = z.object({
      firstname: z.string().min(1, t("min_value", {
        value: 1
      })),
      lastname: z.string().min(1, t("min_value", {
        value: 1
      })),
      email: z.string().trim().refine(_temp4, t("invalid_email")),
      phone: z.string().min(1, t("required")),
      company: z.string().optional(),
      gender: z.string().min(1, t("required")),
      birthDate: z.date({
        error: t("required")
      }),
      timezone: z.string().min(1, t("required")),
      currencyCode: z.string().min(1, t("required")),
      password: z.string().optional(),
      passwordConfirm: z.string().optional()
    }).superRefine((data, ctx) => {
      if (data.password && data.password.length > 0 && data.password !== data.passwordConfirm) {
        ctx.addIssue({
          code: "custom",
          message: t("passwords_do_not_match"),
          path: ["passwordConfirm"]
        });
      }
    });
    t13 = useForm;
    t12 = zodResolver(formSchema);
    $[24] = t;
    $[25] = t12;
    $[26] = t13;
  } else {
    t12 = $[25];
    t13 = $[26];
  }
  const t14 = user.firstname ?? "";
  const t15 = user.lastname ?? "";
  const t16 = user.email ?? "";
  const t17 = user.phone ?? "";
  const t18 = user.company ?? "";
  const t19 = user.gender === GenderEnum.UNKNOWN ? "" : user.gender ?? "";
  let t20;
  if ($[27] !== user.birthDate) {
    t20 = user.birthDate ? new Date(user.birthDate) : undefined;
    $[27] = user.birthDate;
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  const t21 = user.timezone ?? "";
  const t22 = user.currencyCode ?? "";
  let t23;
  if ($[29] !== t14 || $[30] !== t15 || $[31] !== t16 || $[32] !== t17 || $[33] !== t18 || $[34] !== t19 || $[35] !== t20 || $[36] !== t21 || $[37] !== t22) {
    t23 = {
      firstname: t14,
      lastname: t15,
      email: t16,
      phone: t17,
      company: t18,
      gender: t19,
      birthDate: t20,
      timezone: t21,
      currencyCode: t22,
      password: "",
      passwordConfirm: ""
    };
    $[29] = t14;
    $[30] = t15;
    $[31] = t16;
    $[32] = t17;
    $[33] = t18;
    $[34] = t19;
    $[35] = t20;
    $[36] = t21;
    $[37] = t22;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  let t24;
  if ($[39] !== t12 || $[40] !== t23) {
    t24 = {
      resolver: t12,
      defaultValues: t23
    };
    $[39] = t12;
    $[40] = t23;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  const form = t13(t24);
  let t25;
  if ($[42] !== editProfile) {
    t25 = async data_0 => await editProfile(data_0);
    $[42] = editProfile;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] !== onSuccess || $[45] !== t) {
    t26 = () => {
      toast.success(t("profile_updated_success"));
      onSuccess?.();
    };
    $[44] = onSuccess;
    $[45] = t;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  let t27;
  if ($[47] !== form.setError || $[48] !== t) {
    t27 = error => {
      if (error.response?.status === 422) {
        setValidationErrors(error, form.setError);
      } else {
        toast.error(getErrorMessage(error, t("profile_updated_error")));
      }
    };
    $[47] = form.setError;
    $[48] = t;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => {
      setLoading(false);
    };
    $[50] = t28;
  } else {
    t28 = $[50];
  }
  let t29;
  if ($[51] !== t25 || $[52] !== t26 || $[53] !== t27) {
    t29 = {
      mutationFn: t25,
      onSuccess: t26,
      onError: t27,
      onSettled: t28
    };
    $[51] = t25;
    $[52] = t26;
    $[53] = t27;
    $[54] = t29;
  } else {
    t29 = $[54];
  }
  const {
    mutate
  } = useMutation(t29);
  let t30;
  let t31;
  if ($[55] !== loading || $[56] !== onPendingChange) {
    t30 = () => {
      onPendingChange?.(loading);
    };
    t31 = [loading, onPendingChange];
    $[55] = loading;
    $[56] = onPendingChange;
    $[57] = t30;
    $[58] = t31;
  } else {
    t30 = $[57];
    t31 = $[58];
  }
  useEffect(t30, t31);
  let t32;
  if ($[59] !== mutate) {
    t32 = data_1 => {
      setLoading(true);
      const payload = {
        firstname: data_1.firstname,
        lastname: data_1.lastname,
        email: data_1.email,
        phone: data_1.phone || undefined,
        company: data_1.company || undefined,
        gender: data_1.gender as GenderEnum,
        birthDate: data_1.birthDate.toISOString().split("T")[0],
        timezone: data_1.timezone,
        currencyCode: data_1.currencyCode
      };
      if (data_1.password && data_1.password.length > 0) {
        payload.password = data_1.password;
        payload.passwordConfirm = data_1.passwordConfirm;
      }
      mutate(payload);
    };
    $[59] = mutate;
    $[60] = t32;
  } else {
    t32 = $[60];
  }
  const onSubmit = t32;
  let t33;
  if ($[61] !== form || $[62] !== onSubmit) {
    t33 = form.handleSubmit(onSubmit);
    $[61] = form;
    $[62] = onSubmit;
    $[63] = t33;
  } else {
    t33 = $[63];
  }
  let t34;
  if ($[64] !== loading || $[65] !== t) {
    t34 = t35 => {
      const {
        field
      } = t35;
      return <FormItem><FormLabel required={true} htmlFor="profile-firstname">{t("firstname")}</FormLabel><FormControl><InputGroup><InputGroupInput id="profile-firstname" placeholder={t("enter_your_firstname")} {...field} disabled={loading} /></InputGroup></FormControl><FormMessage /></FormItem>;
    };
    $[64] = loading;
    $[65] = t;
    $[66] = t34;
  } else {
    t34 = $[66];
  }
  let t35;
  if ($[67] !== form.control || $[68] !== t34) {
    t35 = <FormField control={form.control} name="firstname" render={t34} />;
    $[67] = form.control;
    $[68] = t34;
    $[69] = t35;
  } else {
    t35 = $[69];
  }
  let t36;
  if ($[70] !== loading || $[71] !== t) {
    t36 = t37 => {
      const {
        field: field_0
      } = t37;
      return <FormItem><FormLabel required={true} htmlFor="profile-lastname">{t("lastname")}</FormLabel><FormControl><InputGroup><InputGroupInput id="profile-lastname" placeholder={t("enter_your_lastname")} {...field_0} disabled={loading} /></InputGroup></FormControl><FormMessage /></FormItem>;
    };
    $[70] = loading;
    $[71] = t;
    $[72] = t36;
  } else {
    t36 = $[72];
  }
  let t37;
  if ($[73] !== form.control || $[74] !== t36) {
    t37 = <FormField control={form.control} name="lastname" render={t36} />;
    $[73] = form.control;
    $[74] = t36;
    $[75] = t37;
  } else {
    t37 = $[75];
  }
  let t38;
  if ($[76] !== t35 || $[77] !== t37) {
    t38 = <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">{t35}{t37}</div>;
    $[76] = t35;
    $[77] = t37;
    $[78] = t38;
  } else {
    t38 = $[78];
  }
  let t39;
  if ($[79] !== loading || $[80] !== t) {
    t39 = t40 => {
      const {
        field: field_1
      } = t40;
      return <FormItem><FormLabel required={true} htmlFor="profile-email">{t("email")}</FormLabel><FormControl><InputGroup><InputGroupInput id="profile-email" type="email" placeholder={t("enter_your_email")} {...field_1} disabled={loading} /><InputGroupAddon><AtSignIcon /></InputGroupAddon></InputGroup></FormControl><FormMessage /></FormItem>;
    };
    $[79] = loading;
    $[80] = t;
    $[81] = t39;
  } else {
    t39 = $[81];
  }
  let t40;
  if ($[82] !== form.control || $[83] !== t39) {
    t40 = <FormField control={form.control} name="email" render={t39} />;
    $[82] = form.control;
    $[83] = t39;
    $[84] = t40;
  } else {
    t40 = $[84];
  }
  let t41;
  if ($[85] !== loading || $[86] !== t) {
    t41 = t42 => {
      const {
        field: field_2
      } = t42;
      return <FormItem><FormLabel required={true} htmlFor="profile-phone">{t("phone")}</FormLabel><FormControl><PhoneInput id="profile-phone" international={true} value={field_2.value} onChange={field_2.onChange} disabled={loading} /></FormControl><FormMessage /></FormItem>;
    };
    $[85] = loading;
    $[86] = t;
    $[87] = t41;
  } else {
    t41 = $[87];
  }
  let t42;
  if ($[88] !== form.control || $[89] !== t41) {
    t42 = <FormField control={form.control} name="phone" render={t41} />;
    $[88] = form.control;
    $[89] = t41;
    $[90] = t42;
  } else {
    t42 = $[90];
  }
  let t43;
  if ($[91] !== t40 || $[92] !== t42) {
    t43 = <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">{t40}{t42}</div>;
    $[91] = t40;
    $[92] = t42;
    $[93] = t43;
  } else {
    t43 = $[93];
  }
  let t44;
  if ($[94] !== loading || $[95] !== t) {
    t44 = t45 => {
      const {
        field: field_3
      } = t45;
      return <FormItem><FormLabel htmlFor="profile-company">{t("company")}</FormLabel><FormControl><InputGroup><InputGroupInput id="profile-company" placeholder={t("enter_your_company")} {...field_3} disabled={loading} /><InputGroupAddon><BuildingIcon /></InputGroupAddon></InputGroup></FormControl><FormMessage /></FormItem>;
    };
    $[94] = loading;
    $[95] = t;
    $[96] = t44;
  } else {
    t44 = $[96];
  }
  let t45;
  if ($[97] !== form.control || $[98] !== t44) {
    t45 = <FormField control={form.control} name="company" render={t44} />;
    $[97] = form.control;
    $[98] = t44;
    $[99] = t45;
  } else {
    t45 = $[99];
  }
  let t46;
  if ($[100] !== genderOptions || $[101] !== loading || $[102] !== t) {
    t46 = t47 => {
      const {
        field: field_4
      } = t47;
      return <FormItem><FormLabel required={true} htmlFor="profile-gender">{t("gender")}</FormLabel><FormControl><Combobox id="profile-gender" options={genderOptions} value={field_4.value} onValueChange={val_0 => field_4.onChange(val_0 as string)} disabled={loading} clearable={true} /></FormControl><FormMessage /></FormItem>;
    };
    $[100] = genderOptions;
    $[101] = loading;
    $[102] = t;
    $[103] = t46;
  } else {
    t46 = $[103];
  }
  let t47;
  if ($[104] !== form.control || $[105] !== t46) {
    t47 = <FormField control={form.control} name="gender" render={t46} />;
    $[104] = form.control;
    $[105] = t46;
    $[106] = t47;
  } else {
    t47 = $[106];
  }
  let t48;
  if ($[107] !== form.formState.errors.birthDate || $[108] !== loading || $[109] !== t) {
    t48 = t49 => {
      const {
        field: field_5
      } = t49;
      return <FormItem><FormLabel required={true}>{t("date_of_birth")}</FormLabel><FormControl><DatePicker value={field_5.value} onChange={field_5.onChange} disabled={loading} isInvalid={!!form.formState.errors.birthDate} /></FormControl><FormMessage /></FormItem>;
    };
    $[107] = form.formState.errors.birthDate;
    $[108] = loading;
    $[109] = t;
    $[110] = t48;
  } else {
    t48 = $[110];
  }
  let t49;
  if ($[111] !== form.control || $[112] !== t48) {
    t49 = <FormField control={form.control} name="birthDate" render={t48} />;
    $[111] = form.control;
    $[112] = t48;
    $[113] = t49;
  } else {
    t49 = $[113];
  }
  let t50;
  if ($[114] !== t47 || $[115] !== t49) {
    t50 = <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">{t47}{t49}</div>;
    $[114] = t47;
    $[115] = t49;
    $[116] = t50;
  } else {
    t50 = $[116];
  }
  let t51;
  if ($[117] !== loading || $[118] !== t || $[119] !== timezoneOptions) {
    t51 = t52 => {
      const {
        field: field_6
      } = t52;
      return <FormItem><FormLabel required={true} htmlFor="profile-timezone">{t("timezone")}</FormLabel><FormControl><Combobox id="profile-timezone" options={timezoneOptions} value={field_6.value} onValueChange={val_1 => field_6.onChange(val_1 as string)} disabled={loading} clearable={true} /></FormControl><FormMessage /></FormItem>;
    };
    $[117] = loading;
    $[118] = t;
    $[119] = timezoneOptions;
    $[120] = t51;
  } else {
    t51 = $[120];
  }
  let t52;
  if ($[121] !== form.control || $[122] !== t51) {
    t52 = <FormField control={form.control} name="timezone" render={t51} />;
    $[121] = form.control;
    $[122] = t51;
    $[123] = t52;
  } else {
    t52 = $[123];
  }
  let t53;
  if ($[124] !== currencyOptions || $[125] !== loading || $[126] !== t) {
    t53 = t54 => {
      const {
        field: field_7
      } = t54;
      return <FormItem><FormLabel required={true} htmlFor="profile-currency">{t("currency")}</FormLabel><FormControl><Combobox id="profile-currency" options={currencyOptions} value={field_7.value} onValueChange={val_2 => field_7.onChange(val_2 as string)} disabled={loading} clearable={true} /></FormControl><FormMessage /></FormItem>;
    };
    $[124] = currencyOptions;
    $[125] = loading;
    $[126] = t;
    $[127] = t53;
  } else {
    t53 = $[127];
  }
  let t54;
  if ($[128] !== form.control || $[129] !== t53) {
    t54 = <FormField control={form.control} name="currencyCode" render={t53} />;
    $[128] = form.control;
    $[129] = t53;
    $[130] = t54;
  } else {
    t54 = $[130];
  }
  let t55;
  if ($[131] !== t52 || $[132] !== t54) {
    t55 = <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">{t52}{t54}</div>;
    $[131] = t52;
    $[132] = t54;
    $[133] = t55;
  } else {
    t55 = $[133];
  }
  let t56;
  if ($[134] !== loading || $[135] !== t) {
    t56 = t57 => {
      const {
        field: field_8
      } = t57;
      return <FormItem><FormLabel htmlFor="profile-password">{t("new_password")}</FormLabel><FormControl><InputGroup><InputGroupPassword id="profile-password" placeholder={"\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"} {...field_8} disabled={loading} /><InputGroupAddon><KeySquareIcon /></InputGroupAddon></InputGroup></FormControl><FormMessage /></FormItem>;
    };
    $[134] = loading;
    $[135] = t;
    $[136] = t56;
  } else {
    t56 = $[136];
  }
  let t57;
  if ($[137] !== form.control || $[138] !== t56) {
    t57 = <FormField control={form.control} name="password" render={t56} />;
    $[137] = form.control;
    $[138] = t56;
    $[139] = t57;
  } else {
    t57 = $[139];
  }
  let t58;
  if ($[140] !== loading || $[141] !== t) {
    t58 = t59 => {
      const {
        field: field_9
      } = t59;
      return <FormItem><FormLabel htmlFor="profile-password-confirm">{t("confirm_new_password")}</FormLabel><FormControl><InputGroup><InputGroupPassword id="profile-password-confirm" placeholder={"\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"} {...field_9} disabled={loading} /><InputGroupAddon><KeySquareIcon /></InputGroupAddon></InputGroup></FormControl><FormMessage /></FormItem>;
    };
    $[140] = loading;
    $[141] = t;
    $[142] = t58;
  } else {
    t58 = $[142];
  }
  let t59;
  if ($[143] !== form.control || $[144] !== t58) {
    t59 = <FormField control={form.control} name="passwordConfirm" render={t58} />;
    $[143] = form.control;
    $[144] = t58;
    $[145] = t59;
  } else {
    t59 = $[145];
  }
  let t60;
  if ($[146] !== t57 || $[147] !== t59) {
    t60 = <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">{t57}{t59}</div>;
    $[146] = t57;
    $[147] = t59;
    $[148] = t60;
  } else {
    t60 = $[148];
  }
  let t61;
  if ($[149] !== formId || $[150] !== t33 || $[151] !== t38 || $[152] !== t43 || $[153] !== t45 || $[154] !== t50 || $[155] !== t55 || $[156] !== t60) {
    t61 = <form id={formId} onSubmit={t33} className="space-y-4">{t38}{t43}{t45}{t50}{t55}{t60}</form>;
    $[149] = formId;
    $[150] = t33;
    $[151] = t38;
    $[152] = t43;
    $[153] = t45;
    $[154] = t50;
    $[155] = t55;
    $[156] = t60;
    $[157] = t61;
  } else {
    t61 = $[157];
  }
  let t62;
  if ($[158] !== form || $[159] !== t61) {
    t62 = <Form {...form}>{t61}</Form>;
    $[158] = form;
    $[159] = t61;
    $[160] = t62;
  } else {
    t62 = $[160];
  }
  return t62;
};
export default UpdateProfileForm;
function _temp(state) {
  return state.metadata;
}
function _temp2(tz) {
  return {
    label: `(${tz.offset}) ${tz.id}`,
    value: tz.id,
    searchValue: tz.id
  };
}
function _temp3(c) {
  return {
    label: `${c.code} — ${c.name}`,
    value: c.code
  };
}
function _temp4(val) {
  return EMAIL_REGEX.test(val);
}